import { AxiosResponse } from "axios";

import { Shipment } from "../models/shipments";

import API from "../api";
import { CountsContainersObj } from "../models/shipment.count";

import { getFilterDashboard } from "../util/getFilterDashboard";
import { OrderStatus } from "../util/enums";

const ROUTE_NAME = "dashboard";

type ShipmentTrackedMonths = {
  month: string;
  count: number;
};
export interface DashboardShipmentTracked {
  year: number;
  months: ShipmentTrackedMonths[];
}

export interface DashboardTrackedDelayRaking {
  containerId: string;
  newETA: string;
  delayTime: number;
}
export interface Option {
  companyId?: number;
  dateFilter?: string;
  dateFilterType?: string;
  dateFrom?: string;
  dateTo?: string;
  pastShipByDate?: boolean;
  projectName?: string;
  status?: string[];
  withTotal?: boolean;
}

interface Volume {
  measurement?: string;
  total?: string;
}

interface WonLost {
  lost?: number;
  percentWon?: number;
  total?: number;
  won?: number;
}

interface QuotePerformed {
  month?: string;
  year?: number;
  count?: number;
  hour?: number;
}

export interface OrderType {
  type?: string;
  hour?: number;
  year?: number;
  month?: string;
  count?: number;
  status?: string;
  date?: string;
  label?: string;
}

interface OrderPerformed {
  counts?: Array<{
    total?: string;
    month?: string;
    year?: number;
    count?: number;
    hour?: number;
  }>;
  measurement?: string;
}

export interface ContainerDcDestination {
  name: string;
  dcDestination: string;
  count: number;
}

export interface DispatchesDestination {
  cartonQtyDespatched: number;
  name: string;
  palletQtyDespatched: number;
  countDespatched: number;
  countDespatches: number;
}

export interface ContainerByArrivalDate {
  id: number;
  containerNumber: string;
  order: string;
  arrivalDate: Date;
  shipmentId: number;
}

const DashboardService = {
  async getQuoteCount(options?: Option): Promise<AxiosResponse<number>> {
    return await API.get(`/${ROUTE_NAME}/quotes/count`, {
      params: {
        ...getFilterDashboard(options),
        company_id: options?.companyId,
      },
    });
  },
  async getOrdersCount(options?: Option): Promise<AxiosResponse<number>> {
    return await API.get(`/${ROUTE_NAME}/orders/count`, {
      params: {
        ...getFilterDashboard(options),
        company_id: options?.companyId,
      },
    });
  },
  async getContainerActiveCount(options?: Option): Promise<AxiosResponse<number>> {
    return await API.get(`/${ROUTE_NAME}/containers/active/count`, {
      params: {
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
        company_id: options?.companyId,
      },
    });
  },
  async getContainerAvgPerWeekCount(options?: Option): Promise<AxiosResponse<number>> {
    return await API.get(`/${ROUTE_NAME}/containers/average`, {
      params: {
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
        company_id: options?.companyId,
      },
    });
  },
  async getContainerDelayedCount(options?: Option): Promise<AxiosResponse<number>> {
    return await API.get(`/${ROUTE_NAME}/containers/delayed/count`, {
      params: {
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
        company_id: options?.companyId,
      },
    });
  },
  async getContainerWaitingAtGateCount(options?: Option): Promise<AxiosResponse<number>> {
    return await API.get(`/${ROUTE_NAME}/containers/waiting/count/gate`, {
      params: {
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
        company_id: options?.companyId,
      },
    });
  },
  async getContainerShipmentTracking(
    options?: Option
  ): Promise<AxiosResponse<DashboardShipmentTracked[]>> {
    return await API.get(`/${ROUTE_NAME}/shipment/trackings/month/count`, {
      params: {
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
        company_id: options?.companyId,
      },
    });
  },
  async getContainerAverageDelayList(
    options?: Option
  ): Promise<AxiosResponse<DashboardTrackedDelayRaking[]>> {
    return await API.get(`/${ROUTE_NAME}/shipment/trackings/delayed/ranking`, {
      params: {
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
        company_id: options?.companyId,
      },
    });
  },
  async getContainersTrackedCount(options?: Option): Promise<AxiosResponse<any[]>> {
    return await API.get(`/${ROUTE_NAME}/containers/tracked/count`, {
      params: {
        company_id: options?.companyId,
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
      },
    });
  },
  async getTotalContainerCount(options?: Option): Promise<AxiosResponse<number>> {
    return await API.get(`/${ROUTE_NAME}/containers/count`, {
      params: {
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
        company_id: options?.companyId,
      },
    });
  },
  async getQuoteStatusesCount(options?: Option): Promise<AxiosResponse<WonLost>> {
    return await API.get(`/${ROUTE_NAME}/quotes/statuses/count`, {
      params: {
        ...getFilterDashboard(options),
        company_id: options?.companyId,
      },
    });
  },
  async getQuotePerformed(options?: Option): Promise<AxiosResponse<QuotePerformed[]>> {
    return await API.get(`/${ROUTE_NAME}/quotes/won/group/count`, {
      params: {
        company_id: options?.companyId,
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
      },
    });
  },
  async gerOrderByType(options?: Option): Promise<AxiosResponse<OrderType[]>> {
    return await API.get(`/${ROUTE_NAME}/orders/type/group/count`, {
      params: {
        company_id: options?.companyId,
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
      },
    });
  },
  async getOrderPerformed(options?: Option): Promise<AxiosResponse<OrderPerformed>> {
    return await API.get(`/${ROUTE_NAME}/orders/volume/group/sum`, {
      params: {
        company_id: options?.companyId,
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
      },
    });
  },
  async getVideoSurveyCount(options?: Option): Promise<AxiosResponse<number>> {
    return await API.get(`/${ROUTE_NAME}/videosurvey/count`, {
      params: {
        ...getFilterDashboard(options),
        company_id: options?.companyId,
      },
    });
  },
  async getQuoteVolume(options?: Option): Promise<AxiosResponse<Volume>> {
    return await API.get(`/${ROUTE_NAME}/quotes/volume/sum`, {
      params: {
        ...getFilterDashboard(options),
        company_id: options?.companyId,
      },
    });
  },
  async getOrderVolume(options?: Option): Promise<AxiosResponse<Volume>> {
    return await API.get(`/${ROUTE_NAME}/orders/volume/sum`, {
      params: {
        ...getFilterDashboard(options),
        company_id: options?.companyId,
      },
    });
  },
  async getTrackingShipments(options?: Option): Promise<AxiosResponse<Shipment[]>> {
    return await API.get(`/${ROUTE_NAME}/tracking/shipments`, { params: options });
  },
  async getContainersDcDestination(
    options?: Option
  ): Promise<AxiosResponse<ContainerDcDestination[]>> {
    return await API.get(`/${ROUTE_NAME}/shipment/containers/dcDestination`, {
      params: {
        ...getFilterDashboard(options),
        company_id: options?.companyId,
        project_name: options?.projectName,
      },
    });
  },
  async getDespatchesByDestination(
    options?: Option
  ): Promise<AxiosResponse<DispatchesDestination[]>> {
    return await API.get(`/${ROUTE_NAME}/dispatches/order/byDestination`, {
      params: {
        ...getFilterDashboard(options),
        company_id: options?.companyId,
        project_name: options?.projectName,
      },
    });
  },
  async getShipmentsContainers(options?: Option): Promise<AxiosResponse<CountsContainersObj>> {
    return await API.get(`/${ROUTE_NAME}/shipment/count/containers`, {
      params: {
        company_id: options?.companyId,
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
      },
    });
  },
  async getPurchaseOrdersCountGroupedBy(options?: Option): Promise<AxiosResponse<OrderType[]>> {
    return await API.get(`/${ROUTE_NAME}/purchaseorder/countGroupedBy`, {
      params: {
        company_id: options?.companyId,
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
      },
    });
  },
  async getPurchaseOrdersCount(options?: Option): Promise<AxiosResponse<any | number>> {
    return await API.get(`/${ROUTE_NAME}/purchaseorder/count`, {
      params: {
        company_id: options?.companyId,
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
        pastShipByDate: options?.pastShipByDate,
        status: options?.status,
        withTotal: options?.withTotal,
      },
    });
  },
  async getPurchaseOrdersCountGroupedByStatus(options?: Option): Promise<AxiosResponse<any>> {
    return await API.get(`/${ROUTE_NAME}/purchaseorder/countGroupedByStatus`, {
      params: {
        company_id: options?.companyId,
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
      },
    });
  },
  async getLastReceivedPurchaseOrders(options?: Option): Promise<AxiosResponse<any>> {
    return await API.get(`/${ROUTE_NAME}/purchaseorder/lastReceivedOrders`, {
      params: {
        company_id: options?.companyId,
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
      },
    });
  },
  async getShipmentsCountGroupedByStatus(options?: Option): Promise<AxiosResponse<any>> {
    return await API.get(`/${ROUTE_NAME}/shipment/countGroupedByStatus`, {
      params: {
        company_id: options?.companyId,
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
      },
    });
  },
  async getContainersCountGroupedByStatus(options?: Option): Promise<AxiosResponse<any>> {
    return await API.get(`/${ROUTE_NAME}/containers/countGroupedByStatus`, {
      params: {
        company_id: options?.companyId,
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
      },
    });
  },
  async getContainersCountGroupedByDestination(options?: Option): Promise<AxiosResponse<any>> {
    return await API.get(`/${ROUTE_NAME}/containers/countGroupedByDestination`, {
      params: {
        company_id: options?.companyId,
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
      },
    });
  },
  async getContainersByArrivalDate(
    options?: Option
  ): Promise<AxiosResponse<ContainerByArrivalDate[]>> {
    return await API.get(`/${ROUTE_NAME}/containers/byArrivalDate`, {
      params: {
        company_id: options?.companyId,
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
      },
    });
  },
  async getProductsCountGroupedByCountry(options?: Option): Promise<AxiosResponse<any>> {
    return await API.get(`/${ROUTE_NAME}/products/countGroupedByCountry`, {
      params: {
        company_id: options?.companyId,
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
      },
    });
  },
  async getProductsMostRequested(options?: Option): Promise<AxiosResponse<any>> {
    return await API.get(`/${ROUTE_NAME}/products/mostRequested`, {
      params: {
        company_id: options?.companyId,
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
      },
    });
  },
  async getProductsRunningOutOfStock(options?: Option): Promise<AxiosResponse<any>> {
    return await API.get(`/${ROUTE_NAME}/products/runningOutOfStock`, {
      params: {
        company_id: options?.companyId,
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
      },
    });
  },
  async getShipmentsCountGroupedByTime(options?: Option): Promise<AxiosResponse<any>> {
    return await API.get(`/${ROUTE_NAME}/shipment/countGroupedByTime`, {
      params: {
        company_id: options?.companyId,
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
      },
    });
  },
  async getShipmentsTrackeds(options?: Option): Promise<AxiosResponse<any>> {
    return await API.get(`/${ROUTE_NAME}/shipment/trackeds`, {
      params: {
        company_id: options?.companyId,
        date_filter: options?.dateFilter,
        date_filter_type: options?.dateFilterType,
        date_from: options?.dateFrom,
        date_to: options?.dateTo,
      },
    });
  },
};

export default DashboardService;
